import { demoAccounts, DemoUserProfile } from './demo-accounts';
import type { JourneyEvent, StartupMetrics } from './mock';

// ── Inline types — no imports from services to avoid circular deps ─────
// Services import from this file, so we must not import back from them.

interface QuestionShape {
  _id?: string;
  type: 'mcq' | 'fill_blank' | 'true_false';
  question: string;
  options: string[];
  correctAnswer: string;
}

interface ItemShape {
  _id?: string;
  title: string;
  type: 'text' | 'pdf' | 'video' | 'quiz';
  order: number;
  textContent: string;
  fileUrl: string;
  fileKey: string;
  fileName: string;
  fileSize: number;
  duration: number;
  quiz: { passingScore: number; questions: QuestionShape[] };
}

interface ModuleShape {
  _id?: string;
  title: string;
  description: string;
  order: number;
  items: ItemShape[];
}

interface CourseShape {
  _id: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  thumbnailKey: string;
  category: string;
  level: string;
  price?: string;
  status: 'draft' | 'published' | 'archived';
  createdBy: string;
  modules: ModuleShape[];
  createdAt: string;
  updatedAt: string;
}

interface EnrollmentShape {
  _id: string;
  userId: string;
  courseId: { _id: string; title: string; category: string; level: string; price?: string; modules: Array<{ items: Array<{ _id: string }> }> };
  enrolledAt: string;
  completedItems: string[];
  completedAt: string | null;
  lastAccessedAt: string;
}

interface StartupShape {
  _id: string;
  founderId: string;
  name: string;
  tagline: string;
  description: string;
  industry: string;
  stage: string;
  website: string;
  teamSize: number;
  foundingYear: number;
  problemStatement: string;
  solution: string;
  targetMarket: string;
  revenueModel: string;
  traction: string;
  documents: { _id: string; title: string; fileUrl: string; fileKey: string; fileName: string; fileSize: number; uploadedAt: string }[];
  metrics: StartupMetrics;
  journey: JourneyEvent[];
  createdAt: string;
  updatedAt: string;
}

interface AnalyticsShape {
  users: { total: number; founders: number; mentors: number; admins: number; investors: number; pendingMentors?: number };
  courses: { total: number; published: number; draft: number; archived: number };
  enrollments: { total: number; completed: number };
  sessions: { total: number; completed: number; scheduled: number; cancelled: number };
  pitches: { totalIdeas: number; approvedIdeas: number; totalPitches: number; approvedPitches: number; publishedPitches: number };
  startups: { total: number; byStage: { idea: number; 'pre-seed': number; seed: number; 'series-a': number } };
  submissions: { total: number; reviewed: number };
  phasePipeline: { phaseId: string; title: string; founderCount: number }[];
  charts: { monthlyUsers: number[]; monthlyEnrollments: number[]; monthlySessions: number[] };
  recentUsers: { _id: string; name: string; role: string; createdAt: string }[];
}

// ── helpers ────────────────────────────────────────────────────────────
let _counter = 100;
export const generateId = () => `gen-${++_counter}`;
export const delay = (ms = 50) => new Promise<void>(r => setTimeout(r, ms));

// ── Users ──────────────────────────────────────────────────────────────
const extraFounders: DemoUserProfile[] = [
  {
    _id: 'f2', firebaseUid: 'demo-f2', email: 'omar@example.com', name: 'Omar Farooq',
    avatar: '', role: 'founder', startups: [], mentees: [], permissions: [],
    bio: 'E-commerce logistics startup founder.', company: 'QuickShip', title: 'CEO',
    phone: '', linkedIn: '', twitter: '', website: '', expertise: [], experience: '',
    specialties: [], region: 'Karachi', industry: ['Logistics'],
    createdAt: '2026-03-05T10:00:00Z', updatedAt: '2026-04-10T08:00:00Z',
  },
  {
    _id: 'f3', firebaseUid: 'demo-f3', email: 'fatima@example.com', name: 'Fatima Noor',
    avatar: '', role: 'founder', startups: [], mentees: [], permissions: [],
    bio: 'HealthTech startup building telemedicine solutions.', company: 'MedConnect', title: 'Founder',
    phone: '', linkedIn: '', twitter: '', website: '', expertise: [], experience: '',
    specialties: [], region: 'Lahore', industry: ['HealthTech'],
    createdAt: '2026-03-10T10:00:00Z', updatedAt: '2026-04-11T08:00:00Z',
  },
  {
    _id: 'f4', firebaseUid: 'demo-f4', email: 'hassan@example.com', name: 'Hassan Ali',
    avatar: '', role: 'founder', startups: [], mentees: [], permissions: [],
    bio: 'EdTech founder working on vocational training platforms.', company: 'SkillBridge', title: 'CEO',
    phone: '', linkedIn: '', twitter: '', website: '', expertise: [], experience: '',
    specialties: [], region: 'Islamabad', industry: ['EdTech'],
    createdAt: '2026-03-12T10:00:00Z', updatedAt: '2026-04-09T08:00:00Z',
  },
];

const extraMentors: DemoUserProfile[] = [
  {
    _id: 'm2', firebaseUid: 'demo-m2', email: 'ahsan@mentors.pk', name: 'Ahsan Khan',
    avatar: '', role: 'mentor', mentorStatus: 'approved',
    startups: [], mentees: ['f2', 'f4'], permissions: [],
    bio: 'Scaled three B2B sales teams from zero to national coverage. Now focused on helping early-stage founders nail their first 10 enterprise customers.',
    company: 'Nexus GTM', title: 'Principal · GTM Advisor',
    phone: '+92 321 1122334', linkedIn: 'https://linkedin.com/in/ahsankhan', twitter: '', website: '',
    expertise: ['GTM', 'Enterprise Sales', 'Partnerships'], experience: '12 years',
    specialties: ['B2B Sales', 'Channel Partnerships'], region: 'Karachi',
    industry: ['Technology', 'E-commerce'],
    createdAt: '2026-02-20T10:00:00Z', updatedAt: '2026-04-02T10:00:00Z',
  },
  {
    _id: 'm3', firebaseUid: 'demo-m3', email: 'zara@mentors.pk', name: 'Zara Ahmed',
    avatar: '', role: 'mentor', mentorStatus: 'approved',
    startups: [], mentees: ['f1', 'f3', 'f4'], permissions: [],
    bio: 'Former Director of Product at two Y Combinator companies. Passionate about helping Pakistani founders think user-first.',
    company: 'Product Collective', title: 'Senior Product Mentor',
    phone: '+92 300 9988776', linkedIn: 'https://linkedin.com/in/zaraahmed', twitter: 'https://twitter.com/zaraprod', website: '',
    expertise: ['Product Management', 'UX', 'Roadmapping'], experience: '10 years',
    specialties: ['User Research', 'PRD Writing', 'Prioritization'], region: 'Lahore',
    industry: ['SaaS', 'HealthTech'],
    createdAt: '2026-02-25T10:00:00Z', updatedAt: '2026-04-05T10:00:00Z',
  },
  {
    _id: 'm4', firebaseUid: 'demo-m4', email: 'usman@mentors.pk', name: 'Usman Siddiqui',
    avatar: '', role: 'mentor', mentorStatus: 'approved',
    startups: [], mentees: ['f1'], permissions: [],
    bio: 'Staff engineer turned fractional CTO. Architected payment rails for three scale-ups including a unicorn-stage fintech.',
    company: 'Siddiqui Systems', title: 'Fractional CTO',
    phone: '+92 345 5544332', linkedIn: 'https://linkedin.com/in/usmansiddiqui', twitter: '', website: '',
    expertise: ['Engineering', 'Infrastructure', 'System Design'], experience: '18 years',
    specialties: ['Scale', 'Hiring Engineers', 'Tech Debt'], region: 'Islamabad',
    industry: ['Infrastructure', 'Technology'],
    createdAt: '2026-03-01T10:00:00Z', updatedAt: '2026-04-03T10:00:00Z',
  },
  {
    _id: 'm5', firebaseUid: 'demo-m5', email: 'sana@mentors.pk', name: 'Dr. Sana Rehman',
    avatar: '', role: 'mentor', mentorStatus: 'approved',
    startups: [], mentees: ['f1', 'f2', 'f3', 'f4'], permissions: [],
    bio: 'Ex-investment banker turned startup CFO. Closed $200M+ across Series A-C rounds. Writes the fundraising playbook nobody taught you.',
    company: 'Rehman Advisory', title: 'CFO in Residence',
    phone: '+92 311 2233445', linkedIn: 'https://linkedin.com/in/sanarehman', twitter: '', website: '',
    expertise: ['Finance', 'Fundraising', 'Unit Economics'], experience: '14 years',
    specialties: ['Term Sheets', 'Financial Modeling', 'Investor Relations'], region: 'Karachi',
    industry: ['FinTech'],
    createdAt: '2026-02-28T10:00:00Z', updatedAt: '2026-04-07T10:00:00Z',
  },
  {
    _id: 'm6', firebaseUid: 'demo-m6', email: 'bilal.iqbal@mentors.pk', name: 'Bilal Iqbal',
    avatar: '', role: 'mentor', mentorStatus: 'approved',
    startups: [], mentees: ['f2', 'f3'], permissions: [],
    bio: 'Growth operator who led 10x growth at two consumer apps. Specialty: paid acquisition + viral loops in low-CPI markets.',
    company: 'Loop Growth', title: 'Head of Growth · Advisor',
    phone: '+92 333 6677889', linkedIn: 'https://linkedin.com/in/bilaliqbal', twitter: 'https://twitter.com/bilalgrowth', website: '',
    expertise: ['Growth', 'Marketing', 'Paid Acquisition'], experience: '9 years',
    specialties: ['Viral Loops', 'CAC/LTV', 'Experiment Velocity'], region: 'Lahore',
    industry: ['E-commerce', 'CleanTech'],
    createdAt: '2026-03-10T10:00:00Z', updatedAt: '2026-04-08T10:00:00Z',
  },
];

const extraInvestors: DemoUserProfile[] = [
  {
    _id: 'i2', firebaseUid: 'demo-i2', email: 'hira@indusvalleyvc.pk', name: 'Hira Baig',
    avatar: '', role: 'investor',
    startups: [], mentees: [], permissions: [],
    bio: 'Seed-stage investor backing founders building for the next 500 million internet users. Thesis-driven, high conviction, few bets.',
    company: 'Indus Valley VC', title: 'Partner',
    phone: '+92 321 4455667', linkedIn: '', twitter: '', website: '',
    expertise: ['Venture Capital', 'Thesis Investing'], experience: '8 years',
    specialties: ['Market Entry', 'Go-to-Market Strategy'], region: 'Karachi',
    industry: ['Technology', 'EdTech'],
    createdAt: '2026-03-02T10:00:00Z', updatedAt: '2026-04-04T10:00:00Z',
  },
  {
    _id: 'i3', firebaseUid: 'demo-i3', email: 'faisal@pakcapital.pk', name: 'Faisal Raza',
    avatar: '', role: 'investor',
    startups: [], mentees: [], permissions: [],
    bio: 'Series A / B investor. Board seats across HealthTech and FinTech portfolio. Focused on scalable, regulated industries.',
    company: 'Pak Capital', title: 'Managing Director',
    phone: '+92 345 7788990', linkedIn: '', twitter: '', website: '',
    expertise: ['Growth Equity', 'Governance'], experience: '22 years',
    specialties: ['Board Operations', 'Scale Financing'], region: 'Islamabad',
    industry: ['HealthTech', 'FinTech'],
    createdAt: '2026-03-05T10:00:00Z', updatedAt: '2026-04-06T10:00:00Z',
  },
  {
    _id: 'i4', firebaseUid: 'demo-i4', email: 'omar@angel.pk', name: 'Omar Javed',
    avatar: '', role: 'investor',
    startups: [], mentees: [], permissions: [],
    bio: 'Angel investor writing $10K-$50K first checks. Ex-operator, three exits. Believes in founders first, markets second.',
    company: 'Independent', title: 'Angel Investor',
    phone: '+92 300 2211334', linkedIn: '', twitter: '', website: '',
    expertise: ['Angel Investing', 'Operator Advice'], experience: '15 years',
    specialties: ['Pre-seed', 'Founder Coaching'], region: 'Lahore',
    industry: ['Technology', 'E-commerce'],
    createdAt: '2026-03-08T10:00:00Z', updatedAt: '2026-04-01T10:00:00Z',
  },
  {
    _id: 'i5', firebaseUid: 'demo-i5', email: 'mariam@greenventures.pk', name: 'Mariam Hussain',
    avatar: '', role: 'investor',
    startups: [], mentees: [], permissions: [],
    bio: 'Climate-first fund investing in Pakistani founders tackling the hardest environmental problems. Patient capital, long horizon.',
    company: 'Green Ventures', title: 'Partner',
    phone: '+92 311 5544667', linkedIn: '', twitter: '', website: '',
    expertise: ['Impact Investing', 'Climate Tech'], experience: '18 years',
    specialties: ['Deep Tech Diligence', 'Mission Alignment'], region: 'Karachi',
    industry: ['Technology', 'CleanTech'],
    createdAt: '2026-03-10T10:00:00Z', updatedAt: '2026-04-02T10:00:00Z',
  },
  {
    _id: 'i6', firebaseUid: 'demo-i6', email: 'ahmad@paperstore.pk', name: 'Ahmad Saleem',
    avatar: '', role: 'investor',
    startups: [], mentees: [], permissions: [],
    bio: 'Pre-seed investor focused on rural innovation. Previously led a $50M agri-fund, now investing personally in AgriTech and CleanTech.',
    company: 'Paperstore Capital', title: 'Founding Investor',
    phone: '+92 333 8899001', linkedIn: '', twitter: '', website: '',
    expertise: ['Pre-seed', 'Rural Tech'], experience: '12 years',
    specialties: ['Agri Supply Chains', 'Rural Distribution'], region: 'Islamabad',
    industry: ['AgriTech', 'CleanTech'],
    createdAt: '2026-03-15T10:00:00Z', updatedAt: '2026-04-09T10:00:00Z',
  },
];

export const users: DemoUserProfile[] = [...demoAccounts, ...extraFounders, ...extraMentors, ...extraInvestors];

// ── Courses ────────────────────────────────────────────────────────────
const mkQ = (q: string, opts: string[], ans: string, type: QuestionShape['type'] = 'mcq'): QuestionShape => ({
  type, question: q, options: opts, correctAnswer: ans,
});

const mkModules = (mods: Array<{ title: string; desc: string; items: Array<Partial<ItemShape>> }>): ModuleShape[] =>
  mods.map((m, mi) => ({
    _id: `mod-${mi}`,
    title: m.title,
    description: m.desc,
    order: mi + 1,
    items: m.items.map((it, ii) => ({
      _id: `item-${mi}-${ii}`,
      title: it.title || '',
      type: it.type || 'text',
      order: ii + 1,
      textContent: it.textContent || '',
      fileUrl: it.fileUrl || '',
      fileKey: '',
      fileName: '',
      fileSize: 0,
      duration: it.duration || 0,
      quiz: it.quiz || { passingScore: 70, questions: [] },
      ...it,
    } as ItemShape)),
  }));

export const courses: CourseShape[] = [
  {
    _id: 'c1', title: 'Startup Fundamentals', description: 'Learn the basics of starting a business — idea validation, customer discovery, and lean methodology.',
    thumbnailUrl: '', thumbnailKey: '', category: 'Business', level: 'Beginner', status: 'published', createdBy: 'a1',
    modules: mkModules([
      { title: 'Lean Startup Basics', desc: 'Core lean concepts', items: [
        { title: 'What is Lean Startup?', type: 'text', textContent: 'The lean startup methodology emphasizes validated learning and rapid iteration...' },
        { title: 'Customer Discovery', type: 'video', fileUrl: '/demo/video1.mp4', duration: 900 },
        { title: 'Quiz: Lean Basics', type: 'quiz', quiz: { passingScore: 70, questions: [
          mkQ('What is the primary goal of an MVP?', ['Maximum features', 'Test assumptions quickly', 'Impress investors', 'Generate revenue'], 'Test assumptions quickly'),
          mkQ('Customer discovery focuses on understanding ___', ['Technology', 'Customer problems', 'Competitors', 'Pricing'], 'Customer problems'),
        ] } },
      ] },
      { title: 'Business Model Canvas', desc: 'Map your business model', items: [
        { title: 'The 9 Building Blocks', type: 'text', textContent: 'The Business Model Canvas consists of 9 blocks: Customer Segments, Value Propositions...' },
        { title: 'Canvas Workshop', type: 'video', fileUrl: '/demo/video2.mp4', duration: 1200 },
      ] },
    ]),
    createdAt: '2026-02-01T10:00:00Z', updatedAt: '2026-03-15T10:00:00Z',
  },
  {
    _id: 'c2', title: 'Market Research & Validation', description: 'Learn to validate your market opportunity and size your addressable market.',
    thumbnailUrl: '', thumbnailKey: '', category: 'Strategy', level: 'Beginner', status: 'published', createdBy: 'a1',
    modules: mkModules([
      { title: 'TAM/SAM/SOM', desc: 'Market sizing techniques', items: [
        { title: 'Market Sizing Fundamentals', type: 'text', textContent: 'Total Addressable Market (TAM) represents the total revenue opportunity...' },
        { title: 'Sizing Exercise', type: 'video', fileUrl: '/demo/video3.mp4', duration: 600 },
        { title: 'Quiz: Market Sizing', type: 'quiz', quiz: { passingScore: 70, questions: [
          mkQ('TAM stands for?', ['Total Active Market', 'Total Addressable Market', 'Target Audience Metrics', 'Total Annual Margin'], 'Total Addressable Market'),
        ] } },
      ] },
    ]),
    createdAt: '2026-02-10T10:00:00Z', updatedAt: '2026-03-20T10:00:00Z',
  },
  {
    _id: 'c3', title: 'Financial Modeling', description: 'Build financial models for your startup — projections, unit economics, and fundraising metrics.',
    thumbnailUrl: '', thumbnailKey: '', category: 'Finance', level: 'Intermediate', status: 'published', createdBy: 'a1',
    modules: mkModules([
      { title: 'Unit Economics', desc: 'CAC, LTV, margins', items: [
        { title: 'Understanding Unit Economics', type: 'text', textContent: 'Unit economics measures the revenue and costs associated with a single unit of your product...' },
        { title: 'Calculating CAC & LTV', type: 'video', fileUrl: '/demo/video4.mp4', duration: 800 },
      ] },
      { title: 'Revenue Projections', desc: 'Building 3-year models', items: [
        { title: 'Projection Techniques', type: 'text', textContent: 'Bottom-up projections start with individual unit metrics...' },
        { title: 'Spreadsheet Workshop', type: 'video', fileUrl: '/demo/video5.mp4', duration: 1500 },
        { title: 'Quiz: Financial Models', type: 'quiz', quiz: { passingScore: 70, questions: [
          mkQ('LTV/CAC ratio above ___ is generally considered healthy', ['1', '2', '3', '5'], '3'),
          mkQ('True or False: Bottom-up projections are more credible than top-down.', ['True', 'False'], 'True', 'true_false'),
        ] } },
      ] },
    ]),
    createdAt: '2026-02-05T10:00:00Z', updatedAt: '2026-03-18T10:00:00Z',
  },
  {
    _id: 'c4', title: 'Pitch Deck Mastery', description: 'Master the art of pitching your startup to investors.',
    thumbnailUrl: '', thumbnailKey: '', category: 'Presentation', level: 'Intermediate', status: 'published', createdBy: 'a1',
    modules: mkModules([
      { title: 'Storytelling Framework', desc: 'Structure your narrative', items: [
        { title: 'The Hero\'s Journey for Startups', type: 'text', textContent: 'Every great pitch tells a story with a clear problem, solution, and vision...' },
        { title: 'Pitch Deck Anatomy', type: 'video', fileUrl: '/demo/video6.mp4', duration: 1100 },
      ] },
      { title: 'Slide-by-Slide Guide', desc: 'Build each slide', items: [
        { title: 'The 12 Essential Slides', type: 'text', textContent: 'Cover slide, Problem, Solution, Market Size, Business Model, Traction...' },
        { title: 'Quiz: Pitch Essentials', type: 'quiz', quiz: { passingScore: 70, questions: [
          mkQ('What should the first slide after the cover establish?', ['Team', 'Problem', 'Revenue', 'Competition'], 'Problem'),
        ] } },
      ] },
    ]),
    createdAt: '2026-02-15T10:00:00Z', updatedAt: '2026-03-25T10:00:00Z',
  },
  {
    _id: 'c5', title: 'Growth Strategies', description: 'Growth hacking, product-led growth, and scaling channels.',
    thumbnailUrl: '', thumbnailKey: '', category: 'Growth', level: 'Advanced', status: 'published', createdBy: 'a1',
    modules: mkModules([
      { title: 'Growth Frameworks', desc: 'AARRR and beyond', items: [
        { title: 'Pirate Metrics (AARRR)', type: 'text', textContent: 'Acquisition, Activation, Retention, Revenue, Referral...' },
        { title: 'Growth Loops', type: 'video', fileUrl: '/demo/video7.mp4', duration: 700 },
      ] },
    ]),
    createdAt: '2026-02-20T10:00:00Z', updatedAt: '2026-03-28T10:00:00Z',
  },
  {
    _id: 'c6', title: 'Legal Basics for Startups', description: 'Company registration, IP protection, and contracts.',
    thumbnailUrl: '', thumbnailKey: '', category: 'Legal', level: 'Beginner', status: 'draft', createdBy: 'a1',
    modules: mkModules([
      { title: 'Company Registration', desc: 'SECP & FBR', items: [
        { title: 'Registering in Pakistan', type: 'text', textContent: 'The Securities and Exchange Commission of Pakistan (SECP) handles company registration...' },
        { title: 'Tax Requirements', type: 'text', textContent: 'FBR registration, NTN number, and sales tax...' },
      ] },
    ]),
    createdAt: '2026-03-01T10:00:00Z', updatedAt: '2026-04-01T10:00:00Z',
  },
];

// ── Enrollments ────────────────────────────────────────────────────────
export const enrollments: EnrollmentShape[] = [
  {
    _id: 'e1', userId: 'f1',
    courseId: { _id: 'c1', title: 'Startup Fundamentals', category: 'Business', level: 'Beginner', modules: courses[0].modules.map(m => ({ items: m.items.map(i => ({ _id: i._id! })) })) },
    enrolledAt: '2026-03-05T10:00:00Z',
    completedItems: ['item-0-0', 'item-0-1', 'item-1-0'],
    completedAt: null,
    lastAccessedAt: '2026-04-15T09:00:00Z',
  },
  {
    _id: 'e2', userId: 'f1',
    courseId: { _id: 'c3', title: 'Financial Modeling', category: 'Finance', level: 'Intermediate', modules: courses[2].modules.map(m => ({ items: m.items.map(i => ({ _id: i._id! })) })) },
    enrolledAt: '2026-03-08T10:00:00Z',
    completedItems: ['item-0-0', 'item-0-1', 'item-1-0', 'item-1-1', 'item-1-2'],
    completedAt: '2026-04-01T10:00:00Z',
    lastAccessedAt: '2026-04-01T10:00:00Z',
  },
  {
    _id: 'e3', userId: 'f1',
    courseId: { _id: 'c2', title: 'Market Research & Validation', category: 'Strategy', level: 'Beginner', modules: courses[1].modules.map(m => ({ items: m.items.map(i => ({ _id: i._id! })) })) },
    enrolledAt: '2026-03-20T10:00:00Z',
    completedItems: ['item-0-0'],
    completedAt: null,
    lastAccessedAt: '2026-04-14T12:00:00Z',
  },
];

// ── Phases ──────────────────────────────────────────────────────────────
export interface PhaseDoc {
  _id: string;
  id: string;
  order: number;
  title: string;
  type: 'coursework' | 'mentor_session' | 'pitching' | 'investor_review';
  description: string;
  unlockConditions: string[];
  courseIds?: string[];
  sessionId?: string;
}

export const phases: PhaseDoc[] = [
  { _id: 'phase-1', id: 'phase-1', order: 1, title: 'Foundation Coursework', type: 'coursework', description: 'Complete foundational courses on startup basics, market research, and business modeling.', unlockConditions: [], courseIds: ['c1', 'c3'] },
  { _id: 'phase-2', id: 'phase-2', order: 2, title: 'Group Mentorship I', type: 'mentor_session', description: 'Attend your first group mentorship session to discuss your progress and get feedback.', unlockConditions: ['phase-1'], sessionId: 'gs1' },
  { _id: 'phase-3', id: 'phase-3', order: 3, title: 'Advanced Coursework', type: 'coursework', description: 'Dive deeper into pitch preparation, financial modeling, and growth strategies.', unlockConditions: ['phase-2'], courseIds: ['c4', 'c5'] },
  { _id: 'phase-4', id: 'phase-4', order: 4, title: 'Group Mentorship II & III', type: 'mentor_session', description: 'Two more group sessions focused on refining your startup approach.', unlockConditions: ['phase-3'] },
  { _id: 'phase-5', id: 'phase-5', order: 5, title: 'Pitching', type: 'pitching', description: 'Submit your idea, prepare your pitch deck, and get mentor approval.', unlockConditions: ['phase-4'] },
  { _id: 'phase-6', id: 'phase-6', order: 6, title: 'Investor Review', type: 'investor_review', description: 'Your approved pitch is published for investor review and potential funding.', unlockConditions: ['phase-5'] },
];

// ── Phase Progress ─────────────────────────────────────────────────────
export interface PhaseProgressDoc {
  _id: string;
  userId: string;
  phaseId: string;
  status: 'locked' | 'active' | 'completed';
  progress: number;
  completedItems: string[];
  startedAt?: string;
  completedAt?: string;
}

export const phaseProgress: PhaseProgressDoc[] = [
  { _id: 'pp1', userId: 'f1', phaseId: 'phase-1', status: 'active', progress: 70, completedItems: ['c1'], startedAt: '2026-03-05T10:00:00Z' },
  { _id: 'pp2', userId: 'f1', phaseId: 'phase-2', status: 'locked', progress: 0, completedItems: [] },
  { _id: 'pp3', userId: 'f1', phaseId: 'phase-3', status: 'locked', progress: 0, completedItems: [] },
  { _id: 'pp4', userId: 'f1', phaseId: 'phase-4', status: 'locked', progress: 0, completedItems: [] },
  { _id: 'pp5', userId: 'f1', phaseId: 'phase-5', status: 'locked', progress: 0, completedItems: [] },
  { _id: 'pp6', userId: 'f1', phaseId: 'phase-6', status: 'locked', progress: 0, completedItems: [] },
];

// ── Group Sessions ─────────────────────────────────────────────────────
export interface GroupSessionDoc {
  _id: string;
  title: string;
  mentorId: string;
  mentorName: string;
  phaseId: string;
  scheduledAt: string;
  duration: number;
  meetLink: string;
  minParticipants: number;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  attendees: { userId: string; name: string; startupName: string; status: 'invited' | 'confirmed' | 'present' | 'absent'; checkedInAt?: string }[];
  agenda: string;
  notes: string;
}

export const groupSessions: GroupSessionDoc[] = [
  {
    _id: 'gs1', title: 'Lean Validation Workshop', mentorId: 'm1', mentorName: 'Sarah Johnson', phaseId: 'phase-2',
    scheduledAt: '2026-04-05T14:00:00Z', duration: 60, meetLink: 'https://meet.example.com/gs1', minParticipants: 3,
    status: 'completed',
    attendees: [
      { userId: 'f1', name: 'Ayesha Malik', startupName: 'TechFlow Solutions', status: 'present', checkedInAt: '2026-04-05T14:02:00Z' },
      { userId: 'f2', name: 'Omar Farooq', startupName: 'QuickShip', status: 'present', checkedInAt: '2026-04-05T14:01:00Z' },
      { userId: 'f3', name: 'Fatima Noor', startupName: 'MedConnect', status: 'absent' },
    ],
    agenda: 'Review lean canvases, discuss customer interview findings.', notes: 'Great discussion on validation techniques.',
  },
  {
    _id: 'gs2', title: 'Market Sizing Deep-Dive', mentorId: 'm1', mentorName: 'Sarah Johnson', phaseId: 'phase-2',
    scheduledAt: '2026-04-08T14:00:00Z', duration: 45, meetLink: 'https://meet.example.com/gs2', minParticipants: 3,
    status: 'completed',
    attendees: [
      { userId: 'f1', name: 'Ayesha Malik', startupName: 'TechFlow Solutions', status: 'present', checkedInAt: '2026-04-08T14:00:00Z' },
      { userId: 'f2', name: 'Omar Farooq', startupName: 'QuickShip', status: 'present', checkedInAt: '2026-04-08T14:03:00Z' },
      { userId: 'f4', name: 'Hassan Ali', startupName: 'SkillBridge', status: 'present', checkedInAt: '2026-04-08T14:01:00Z' },
    ],
    agenda: 'TAM/SAM/SOM calculations for each startup.', notes: 'Strong market sizing presentations from all founders.',
  },
  {
    _id: 'gs3', title: 'Pitch Preparation Session', mentorId: 'm1', mentorName: 'Sarah Johnson', phaseId: 'phase-4',
    scheduledAt: '2026-04-20T14:00:00Z', duration: 90, meetLink: 'https://meet.example.com/gs3', minParticipants: 3,
    status: 'scheduled',
    attendees: [
      { userId: 'f1', name: 'Ayesha Malik', startupName: 'TechFlow Solutions', status: 'confirmed' },
      { userId: 'f2', name: 'Omar Farooq', startupName: 'QuickShip', status: 'invited' },
      { userId: 'f3', name: 'Fatima Noor', startupName: 'MedConnect', status: 'confirmed' },
    ],
    agenda: 'Practice pitch decks, peer feedback.', notes: '',
  },
  {
    _id: 'gs4', title: 'Growth Strategy Workshop', mentorId: 'm1', mentorName: 'Sarah Johnson', phaseId: 'phase-4',
    scheduledAt: '2026-04-16T10:00:00Z', duration: 60, meetLink: 'https://meet.example.com/gs4', minParticipants: 2,
    status: 'in_progress',
    attendees: [
      { userId: 'f1', name: 'Ayesha Malik', startupName: 'TechFlow Solutions', status: 'present', checkedInAt: '2026-04-16T10:01:00Z' },
      { userId: 'f4', name: 'Hassan Ali', startupName: 'SkillBridge', status: 'present', checkedInAt: '2026-04-16T10:00:00Z' },
    ],
    agenda: 'Growth channels identification and prioritisation.', notes: '',
  },
];

// ── Idea Submissions ───────────────────────────────────────────────────
export interface IdeaDoc {
  _id: string;
  startupId: string;
  founderId: string;
  title: string;
  problemStatement: string;
  proposedSolution: string;
  targetMarket: string;
  uniqueValueProp: string;
  status: 'draft' | 'submitted' | 'approved' | 'revision_needed';
  mentorFeedback?: string;
  submittedAt?: string;
}

export const ideaSubmissions: IdeaDoc[] = [
  {
    _id: 'idea1', startupId: 's1', founderId: 'f1',
    title: 'AI-Powered Workflow Automation',
    problemStatement: 'SMEs in Pakistan waste 30% of productive time on repetitive manual processes.',
    proposedSolution: 'An AI agent platform that learns business workflows and automates them with zero coding.',
    targetMarket: 'Pakistani SMEs with 10-200 employees, focusing on manufacturing and retail.',
    uniqueValueProp: 'Urdu-first interface, local integrations (JazzCash, EasyPaisa), 10x cheaper than Western alternatives.',
    status: 'approved', mentorFeedback: 'Strong problem-solution fit. Market size is compelling. Proceed to pitch.',
    submittedAt: '2026-03-20T10:00:00Z',
  },
  {
    _id: 'idea2', startupId: 's1', founderId: 'f1',
    title: 'Smart Inventory Management',
    problemStatement: 'Retailers lose revenue due to stock-outs and over-ordering.',
    proposedSolution: 'Predictive inventory system powered by sales data analysis.',
    targetMarket: 'Retail shops in tier-2 cities.',
    uniqueValueProp: 'Works offline, SMS-based alerts, no smartphone required.',
    status: 'draft',
  },
  {
    _id: 'idea3', startupId: 's2', founderId: 'f2',
    title: 'Last-Mile Delivery Network',
    problemStatement: 'E-commerce returns in Pakistan cost retailers $120M annually due to failed last-mile deliveries in peri-urban areas.',
    proposedSolution: 'Crowdsourced delivery network using verified part-time riders with real-time tracking and cash-on-delivery support.',
    targetMarket: 'E-commerce companies shipping to Lahore, Faisalabad, and Multan suburbs.',
    uniqueValueProp: 'Local rider network with 98% delivery success rate, 40% cheaper than courier incumbents.',
    status: 'submitted', submittedAt: '2026-04-10T09:00:00Z',
  },
  {
    _id: 'idea4', startupId: 's3', founderId: 'f3',
    title: 'Telemedicine for Rural Pakistan',
    problemStatement: '70% of Pakistan\'s population lacks access to specialist medical care within 50km.',
    proposedSolution: 'Asynchronous teleconsultation platform connecting rural patients to urban specialists via basic smartphones.',
    targetMarket: 'Rural patients in KPK and Punjab, partnering with BISP health programs.',
    uniqueValueProp: 'Works on 2G, supports voice consultations in Pashto and Punjabi, integrates with NADRA verification.',
    status: 'submitted', submittedAt: '2026-04-12T11:00:00Z',
  },
  {
    _id: 'idea5', startupId: 's4', founderId: 'f4',
    title: 'Vocational Micro-Credentials Platform',
    problemStatement: 'Pakistan has 3M TVET graduates annually but 68% remain unemployed due to unverified skills.',
    proposedSolution: 'Blockchain-based micro-credential platform for verified vocational skills, linked to employer hiring portals.',
    targetMarket: 'TVET graduates ages 18-28 and SME employers in manufacturing, construction, and hospitality.',
    uniqueValueProp: 'First verifiable digital credentials for blue-collar workers in Pakistan, partnered with Punjab TEVTA.',
    status: 'revision_needed', mentorFeedback: 'Strong social impact angle. Need clearer monetization model — who pays, employer or graduate?',
    submittedAt: '2026-04-08T14:00:00Z',
  },
];

// ── Pitch Submissions ──────────────────────────────────────────────────
export interface PitchDoc {
  _id: string;
  startupId: string;
  founderId: string;
  ideaSubmissionId: string;
  problem: string;
  solution: string;
  marketSize: string;
  businessModel: string;
  traction: string;
  team: string;
  financials: string;
  askAmount: string;
  useOfFunds: string;
  equityOffered?: string;       // e.g. '12%'
  preMoneyValuation?: string;   // e.g. '$1.75M'
  fileUrl?: string;
  status: 'draft' | 'submitted' | 'in_review' | 'approved' | 'revision_needed';
  publishedForInvestors: boolean;
  mentorNotes?: string;
}

export const pitchSubmissions: PitchDoc[] = [
  {
    _id: 'pitch1', startupId: 's1', founderId: 'f1', ideaSubmissionId: 'idea1',
    problem: 'SMEs in Pakistan waste 30% of productive time on repetitive manual processes, losing $2B annually.',
    solution: 'TechFlow is an AI agent platform that learns workflows and automates them — zero coding, Urdu-first.',
    marketSize: 'TAM: $5B (Pakistan SME software), SAM: $800M (automation tools), SOM: $40M (first 3 years)',
    businessModel: 'SaaS — $49/month per team. Enterprise tier at $199/month. 85% gross margin.',
    traction: '50 beta users, 12 paying customers, $8K MRR, 40% MoM growth.',
    team: 'Ayesha Malik (CEO, ex-Systems Ltd), Alex Khan (CTO, ex-Careem), 4 engineers.',
    financials: 'Burn: $15K/mo. Runway: 14 months. Projecting $500K ARR by EOY.',
    askAmount: '$250,000',
    equityOffered: '12%',
    preMoneyValuation: '$1.83M',
    useOfFunds: '40% Engineering, 30% Sales, 20% Marketing, 10% Operations',
    fileUrl: '/demo/pitch-deck.pdf',
    status: 'approved', publishedForInvestors: true,
    mentorNotes: 'Solid deck. Clear metrics and realistic projections.',
  },
  {
    _id: 'pitch2', startupId: 's1', founderId: 'f1', ideaSubmissionId: 'idea1',
    problem: 'Draft version of the pitch', solution: '', marketSize: '', businessModel: '',
    traction: '', team: '', financials: '', askAmount: '', useOfFunds: '',
    status: 'draft', publishedForInvestors: false,
  },
  {
    _id: 'pitch3', startupId: 's2', founderId: 'f2', ideaSubmissionId: 'idea3',
    problem: 'E-commerce failed deliveries cost Pakistani retailers $120M annually. Rural and peri-urban areas have <60% delivery success rates.',
    solution: 'QuickShip is a crowdsourced last-mile network with 2,000+ verified riders across 8 cities, achieving 97% delivery success.',
    marketSize: 'TAM: $1.8B (Pakistan logistics), SAM: $320M (e-commerce last-mile), SOM: $18M (year 3 target)',
    businessModel: 'Per-delivery fee of Rs 150-250. Volume discounts for enterprise e-commerce accounts. 72% gross margin.',
    traction: '3 enterprise clients (Daraz, Naheed, JumboJet), 2,400 deliveries/day, Rs 36M MRR.',
    team: 'Omar Farooq (CEO, ex-Daraz Ops Lead), Zara Siddiqui (COO, ex-TCS), 8 FTEs.',
    financials: 'Burn: Rs 4.2M/mo. Runway: 18 months. Break-even projected Q3 2026.',
    askAmount: '$180,000',
    equityOffered: '10%',
    preMoneyValuation: '$1.62M',
    useOfFunds: '50% Rider onboarding & tech, 30% City expansion, 20% Working capital',
    status: 'approved', publishedForInvestors: true,
    mentorNotes: '',
  },
  {
    _id: 'pitch4', startupId: 's3', founderId: 'f3', ideaSubmissionId: 'idea4',
    problem: '142M rural Pakistanis lack specialist medical access. 65% of preventable deaths occur in areas with no doctors within 50km.',
    solution: 'MedConnect enables asynchronous teleconsultations via basic smartphones on 2G, with 450 verified specialists on platform.',
    marketSize: 'TAM: $2.3B (Pakistan private health), SAM: $410M (telemedicine), SOM: $22M (rural segment, 5 years)',
    businessModel: 'Rs 300 per consultation. BISP partnership for subsidized access. Pharma data licensing pipeline.',
    traction: '8,200 consultations completed, 94% patient satisfaction, 3 provincial health dept MOUs.',
    team: 'Fatima Noor (CEO, LUMS MBA, ex-Sehat Kahani), Dr. Imran Qureshi (CMO, FCPS), 6 FTEs.',
    financials: 'Burn: Rs 3.8M/mo. Runway: 11 months. $40K MRR, 35% MoM growth.',
    askAmount: '$320,000',
    equityOffered: '15%',
    preMoneyValuation: '$1.81M',
    useOfFunds: '45% Doctor network expansion, 35% Tech & AI triage, 20% Rural marketing',
    fileUrl: '/demo/medconnect-deck.pdf',
    status: 'approved', publishedForInvestors: true,
    mentorNotes: 'Exceptional traction for stage. Impact story resonates strongly with social impact investors.',
  },
];

// ── Startups ───────────────────────────────────────────────────────────
// Seeded business journey for s1 (TechFlow). Spans ~12 months from founding
// through current MRR of $8K and 12 paying customers. Used by the Business
// Journey page to demo the trajectory chart, milestone orbs, and story
// timeline. Other startups get empty arrays — they'll populate as their
// founders edit profiles / upload documents.
const techflowJourney: JourneyEvent[] = [
  // Day Zero
  { id: 'jv-tf-01', startupId: 's1', date: '2025-04-01T09:00:00Z', category: 'milestone',
    title: 'Founded TechFlow Solutions', badge: '✨',
    summary: 'Two co-founders, one laptop, and a hunch about Urdu-first workflow automation.' },

  // First metric / hire
  { id: 'jv-tf-02', startupId: 's1', date: '2025-05-12T10:00:00Z', category: 'metric',
    metricKey: 'teamSize', metricFrom: 2, metricTo: 3, unit: '',
    title: 'First engineer joined', summary: 'Hired Alex Khan (CTO) — the team grows from 2 to 3.' },

  // Vision document upload
  { id: 'jv-tf-03', startupId: 's1', date: '2025-06-04T12:00:00Z', category: 'document',
    documentTitle: 'Vision Document v1', title: 'Vision document published',
    summary: '"Automate the boring half of every Pakistani SME by 2030." Signed by both co-founders.' },

  // First clients
  { id: 'jv-tf-04', startupId: 's1', date: '2025-07-22T10:00:00Z', category: 'metric',
    metricKey: 'clients', metricFrom: 0, metricTo: 3, unit: '',
    title: 'First 3 paying clients', summary: 'Three Karachi-based SMEs signed up for the closed beta.' },
  { id: 'jv-tf-05', startupId: 's1', date: '2025-07-22T10:00:01Z', category: 'metric',
    metricKey: 'mrr', metricFrom: 0, metricTo: 1200, unit: '$',
    title: 'First revenue', summary: 'MRR moved from $0 to $1,200.' },

  // Achievement
  { id: 'jv-tf-06', startupId: 's1', date: '2025-08-15T15:00:00Z', category: 'milestone',
    title: 'Closed first 10 clients', badge: '🏆',
    summary: 'Hit double digits with a single AE and word-of-mouth referrals.' },
  { id: 'jv-tf-07', startupId: 's1', date: '2025-08-15T15:00:01Z', category: 'metric',
    metricKey: 'clients', metricFrom: 3, metricTo: 10, unit: '',
    title: 'Clients: 3 → 10' },

  // Profile change
  { id: 'jv-tf-08', startupId: 's1', date: '2025-09-02T11:00:00Z', category: 'profile',
    field: 'tagline', valueFrom: 'Workflow automation for everyone',
    valueTo: 'Automate your business, the smart way',
    title: 'Tagline rewritten', summary: 'Sharper positioning after sales discovery interviews.' },

  // Partnership
  { id: 'jv-tf-09', startupId: 's1', date: '2025-10-10T10:00:00Z', category: 'metric',
    metricKey: 'partnerships', metricFrom: 0, metricTo: 2, unit: '',
    title: 'First 2 partnerships', summary: 'Reseller agreements with two Karachi accounting firms.' },

  // Mid-journey growth
  { id: 'jv-tf-10', startupId: 's1', date: '2025-11-18T09:30:00Z', category: 'metric',
    metricKey: 'mrr', metricFrom: 1200, metricTo: 3500, unit: '$',
    title: 'MRR: $1.2K → $3.5K', summary: 'Quarter-over-quarter growth from upsells and one new logo.' },

  // Document upload
  { id: 'jv-tf-11', startupId: 's1', date: '2026-03-15T10:00:00Z', category: 'document',
    documentId: 'doc1', documentTitle: 'Business Plan',
    title: 'Business Plan uploaded', summary: '24-page deck covering ICP, motion, and 18-month plan.' },

  // Big achievement
  { id: 'jv-tf-12', startupId: 's1', date: '2026-03-22T14:00:00Z', category: 'milestone',
    title: '100 clients in pipeline', badge: '🚀',
    summary: 'CRM crossed 100 qualified leads — pipeline coverage is finally healthy.' },

  // Sales / MRR / Team in current state
  { id: 'jv-tf-13', startupId: 's1', date: '2026-03-20T10:00:00Z', category: 'document',
    documentId: 'doc2', documentTitle: 'Financial Projections',
    title: 'Financial projections uploaded', summary: '3-year model with bottom-up unit economics.' },
  { id: 'jv-tf-14', startupId: 's1', date: '2026-04-01T10:00:00Z', category: 'metric',
    metricKey: 'mrr', metricFrom: 3500, metricTo: 8000, unit: '$',
    title: 'MRR crossed $8K', summary: '12 paying customers; ARPA ticking up as larger SMEs adopt.' },
  { id: 'jv-tf-15', startupId: 's1', date: '2026-04-05T10:00:00Z', category: 'metric',
    metricKey: 'clients', metricFrom: 10, metricTo: 12, unit: '',
    title: 'Clients: 10 → 12' },
  { id: 'jv-tf-16', startupId: 's1', date: '2026-04-08T10:00:00Z', category: 'metric',
    metricKey: 'teamSize', metricFrom: 3, metricTo: 6, unit: '',
    title: 'Team grew to 6', summary: 'Added two engineers and a customer success lead.' },
  { id: 'jv-tf-17', startupId: 's1', date: '2026-04-09T10:00:00Z', category: 'metric',
    metricKey: 'partnerships', metricFrom: 2, metricTo: 5, unit: '',
    title: 'Partnerships: 2 → 5', summary: 'Three new channel deals — JazzCash integration unlocked them.' },
];

export const startups: StartupShape[] = [
  {
    _id: 's2', founderId: 'f2',
    name: 'QuickShip', tagline: 'Last-mile delivery, everywhere in Pakistan',
    description: 'Crowdsourced last-mile delivery network for e-commerce, covering peri-urban and tier-2 cities.',
    industry: 'Logistics', stage: 'mvp', website: 'https://quickship.pk',
    teamSize: 10, foundingYear: 2025,
    problemStatement: 'Failed last-mile deliveries cost e-commerce retailers $120M annually.',
    solution: 'Verified rider network with real-time tracking and COD support.',
    targetMarket: 'E-commerce companies in Pakistan\'s top 8 cities.',
    revenueModel: 'Per-delivery fee Rs 150-250.',
    traction: '2,400 deliveries/day, 3 enterprise clients',
    documents: [],
    metrics: { mrr: 36000, clients: 3, partnerships: 0, sales: 2400 },
    journey: [],
    createdAt: '2026-03-05T10:00:00Z', updatedAt: '2026-04-10T08:00:00Z',
  },
  {
    _id: 's3', founderId: 'f3',
    name: 'MedConnect', tagline: 'Healthcare without borders',
    description: 'Asynchronous telemedicine platform connecting rural Pakistani patients to urban specialists via basic smartphones.',
    industry: 'HealthTech', stage: 'mvp', website: 'https://medconnect.pk',
    teamSize: 8, foundingYear: 2025,
    problemStatement: '142M rural Pakistanis lack access to specialist medical care.',
    solution: 'Teleconsultations on 2G with multilingual support.',
    targetMarket: 'Rural patients across KPK and Punjab.',
    revenueModel: 'Rs 300 per consultation; BISP partnership.',
    traction: '8,200 consultations, 94% satisfaction',
    documents: [],
    metrics: { mrr: 40000, clients: 8200, partnerships: 3, sales: 8200 },
    journey: [],
    createdAt: '2026-03-10T10:00:00Z', updatedAt: '2026-04-11T08:00:00Z',
  },
  {
    _id: 's4', founderId: 'f4',
    name: 'SkillBridge', tagline: 'Verified skills for Pakistan\'s workforce',
    description: 'Blockchain-based micro-credential platform for vocational graduates, linked to employer hiring portals.',
    industry: 'EdTech', stage: 'idea', website: 'https://skillbridge.pk',
    teamSize: 4, foundingYear: 2026,
    problemStatement: '3M TVET graduates annually with unverified skills.',
    solution: 'Blockchain micro-credentials linked to employer portals.',
    targetMarket: 'TVET graduates 18-28 and SME employers.',
    revenueModel: 'Employer subscription for candidate access.',
    traction: 'Punjab TEVTA MOU signed',
    documents: [],
    metrics: { mrr: 0, clients: 0, partnerships: 1, sales: 0 },
    journey: [],
    createdAt: '2026-03-12T10:00:00Z', updatedAt: '2026-04-09T08:00:00Z',
  },
  {
    _id: 's1', founderId: 'f1',
    name: 'TechFlow Solutions', tagline: 'Automate your business, the smart way',
    description: 'AI-powered workflow automation platform for Pakistani SMEs. We help businesses automate repetitive tasks with zero coding, featuring an Urdu-first interface and local payment integrations.',
    industry: 'Technology', stage: 'mvp', website: 'https://techflow.pk',
    teamSize: 6, foundingYear: 2025,
    problemStatement: 'SMEs waste 30% of productive time on manual processes.',
    solution: 'AI agents that learn and automate business workflows.',
    targetMarket: 'Pakistani SMEs with 10-200 employees.',
    revenueModel: 'SaaS subscription — $49/month per team.',
    traction: '50 beta users, 12 paying, $8K MRR',
    documents: [
      { _id: 'doc1', title: 'Business Plan', fileUrl: '/demo/business-plan.pdf', fileKey: 'bp1', fileName: 'business-plan.pdf', fileSize: 2400000, uploadedAt: '2026-03-15T10:00:00Z' },
      { _id: 'doc2', title: 'Financial Projections', fileUrl: '/demo/financials.xlsx', fileKey: 'fp1', fileName: 'financials.xlsx', fileSize: 450000, uploadedAt: '2026-03-20T10:00:00Z' },
    ],
    metrics: { mrr: 8000, clients: 12, partnerships: 5, sales: 12 },
    journey: techflowJourney,
    createdAt: '2025-04-01T09:00:00Z', updatedAt: '2026-04-10T08:00:00Z',
  },
];

// ── Investor Interest ──────────────────────────────────────────────────
export interface InvestorInterestDoc {
  _id: string;
  pitchId: string;
  investorId: string;
  investorName: string;
  expressedAt: string;
}

export const investorInterests: InvestorInterestDoc[] = [
  { _id: 'ii2', pitchId: 'pitch4', investorId: 'i1', investorName: 'Kamran Sheikh', expressedAt: '2026-04-14T10:00:00Z' },
  { _id: 'ii3', pitchId: 'pitch1', investorId: 'i2', investorName: 'Hira Baig', expressedAt: '2026-04-13T10:00:00Z' },
  { _id: 'ii4', pitchId: 'pitch4', investorId: 'i2', investorName: 'Hira Baig', expressedAt: '2026-04-15T10:00:00Z' },
  { _id: 'ii5', pitchId: 'pitch1', investorId: 'i3', investorName: 'Faisal Raza', expressedAt: '2026-04-16T10:00:00Z' },
  { _id: 'ii6', pitchId: 'pitch4', investorId: 'i3', investorName: 'Faisal Raza', expressedAt: '2026-04-17T10:00:00Z' },
  { _id: 'ii7', pitchId: 'pitch4', investorId: 'i5', investorName: 'Mariam Hussain', expressedAt: '2026-04-18T10:00:00Z' },
];

// ── Institution Partners ───────────────────────────────────────────────
export interface PartnerDoc {
  _id: string;
  institutionName: string;
  type: 'University' | 'Accelerator' | 'Government' | 'Corporate';
  location: string;
  region: string;
  studentCount: number;
  primaryContact: string;
  contactEmail: string;
  website: string;
  joinedAt: string;
}

export const partners: PartnerDoc[] = [
  {
    _id: 'pt1', institutionName: 'Institute of Business Administration, Karachi', type: 'University',
    location: 'Karachi', region: 'Sindh', studentCount: 80,
    primaryContact: 'Dr. Mehmood Ahmed', contactEmail: 'mehmood.iba@founderlab.pk',
    website: 'https://iba.edu.pk', joinedAt: '2024-09-12T10:00:00Z',
  },
  {
    _id: 'pt2', institutionName: 'Lahore University of Management Sciences', type: 'University',
    location: 'Lahore', region: 'Punjab', studentCount: 65,
    primaryContact: 'Dr. Sadia Masood', contactEmail: 'sadia.lums@founderlab.pk',
    website: 'https://lums.edu.pk', joinedAt: '2024-11-03T10:00:00Z',
  },
  {
    _id: 'pt3', institutionName: 'NED University of Engineering & Technology', type: 'University',
    location: 'Karachi', region: 'Sindh', studentCount: 45,
    primaryContact: 'Eng. Naveed Hussain', contactEmail: 'naveed.ned@founderlab.pk',
    website: 'https://neduet.edu.pk', joinedAt: '2025-02-18T10:00:00Z',
  },
  {
    _id: 'pt4', institutionName: 'National Incubation Center Islamabad', type: 'Accelerator',
    location: 'Islamabad', region: 'Federal', studentCount: 120,
    primaryContact: 'Zain Anwar', contactEmail: 'zain.nic@founderlab.pk',
    website: 'https://nic.com.pk', joinedAt: '2024-08-05T10:00:00Z',
  },
  {
    _id: 'pt5', institutionName: 'Ignite Fund — Ministry of IT & Telecom', type: 'Government',
    location: 'Islamabad', region: 'Federal', studentCount: 200,
    primaryContact: 'Shumaila Khan', contactEmail: 'shumaila.ignite@founderlab.pk',
    website: 'https://ignite.org.pk', joinedAt: '2023-10-22T10:00:00Z',
  },
];

// ── Invites ────────────────────────────────────────────────────────────
export interface InviteDoc {
  _id: string;
  token: string;
  email: string;
  name: string;
  role: 'mentor' | 'investor';
  status: 'pending' | 'used' | 'expired';
  createdBy: string;
  createdAt: string;
  usedAt?: string;
}

export const invites: InviteDoc[] = [
  { _id: 'inv1', token: 'tok-mentor-abc', email: 'sarah@founderlab.pk', name: 'Sarah Johnson', role: 'mentor', status: 'used', createdBy: 'a1', createdAt: '2026-02-10T10:00:00Z', usedAt: '2026-02-15T10:00:00Z' },
  { _id: 'inv2', token: 'tok-investor-xyz', email: 'newguy@invest.pk', name: 'New Investor', role: 'investor', status: 'pending', createdBy: 'a1', createdAt: '2026-04-10T10:00:00Z' },
];

// ── Analytics snapshot ─────────────────────────────────────────────────
export const analyticsSnapshot: AnalyticsShape = {
  users: { total: 7, founders: 4, mentors: 1, admins: 1, investors: 1 },
  courses: { total: 6, published: 5, draft: 1, archived: 0 },
  enrollments: { total: 3, completed: 1 },
  sessions: { total: 4, completed: 2, scheduled: 1, cancelled: 0 },
  pitches: { totalIdeas: 2, approvedIdeas: 1, totalPitches: 2, approvedPitches: 1, publishedPitches: 1 },
  startups: { total: 1, byStage: { idea: 0, 'pre-seed': 0, seed: 0, 'series-a': 0 } },
  submissions: { total: 4, reviewed: 2 },
  phasePipeline: [
    { phaseId: 'phase-1', title: 'Foundation Coursework', founderCount: 4 },
    { phaseId: 'phase-2', title: 'Group Mentorship I', founderCount: 0 },
    { phaseId: 'phase-3', title: 'Advanced Coursework', founderCount: 0 },
    { phaseId: 'phase-4', title: 'Group Mentorship II & III', founderCount: 0 },
    { phaseId: 'phase-5', title: 'Pitching', founderCount: 0 },
    { phaseId: 'phase-6', title: 'Investor Review', founderCount: 0 },
  ],
  charts: { monthlyUsers: [1, 2, 3, 4, 5, 6, 7], monthlyEnrollments: [0, 1, 2, 3, 3, 3, 3], monthlySessions: [0, 0, 1, 2, 3, 4, 4] },
  recentUsers: [
    { _id: 'f4', name: 'Hassan Ali', role: 'founder', createdAt: '2026-03-12T10:00:00Z' },
    { _id: 'f3', name: 'Fatima Noor', role: 'founder', createdAt: '2026-03-10T10:00:00Z' },
    { _id: 'f2', name: 'Omar Farooq', role: 'founder', createdAt: '2026-03-05T10:00:00Z' },
    { _id: 'f1', name: 'Ayesha Malik', role: 'founder', createdAt: '2026-03-01T10:00:00Z' },
  ],
};
